import React, { useState, useRef, useEffect } from "react";
import FleetVehicleSelect from "../VehicleManagement/FleetVehicleSelect";

const DriverLiveTracking = () => {
  const [location, setLocation] = useState(null);
  const [tracking, setTracking] = useState(false);
  const [error, setError] = useState("");
  const [history, setHistory] = useState([]);
  const [driverName, setDriverName] = useState("");
  const [vehicleNo, setVehicleNo] = useState("");
  const watchIdRef = useRef(null);

  const handleStart = () => {
    if (!navigator.geolocation) { setError("Geolocation not supported by this browser."); return; }
    setError("");
    watchIdRef.current = navigator.geolocation.watchPosition(
      (pos) => {
        const coords = {
          lat: pos.coords.latitude,
          lng: pos.coords.longitude,
          accuracy: pos.coords.accuracy?.toFixed(0),
          time: new Date().toLocaleTimeString(),
        };
        setLocation(coords);
        setHistory((prev) => [coords, ...prev].slice(0, 15));
      },
      (err) => setError(`Location error: ${err.message}`),
      { enableHighAccuracy: true, maximumAge: 0 }
    );
    setTracking(true);
  };

  const handleStop = () => {
    if (watchIdRef.current !== null) navigator.geolocation.clearWatch(watchIdRef.current);
    setTracking(false);
  };

  useEffect(() => () => {
    if (watchIdRef.current) navigator.geolocation.clearWatch(watchIdRef.current);
  }, []);

  const s = {
    container: {
      fontFamily: "Arial, sans-serif",
      marginLeft: "278px",
      marginRight: "10px",
      backgroundColor: "#ffffff",
      color: "#000000",
      minHeight: "100vh",
    },
    pageHeader: {
      backgroundColor: "#FFC107",
      padding: "16px 24px",
      marginBottom: "0px",
    },
    pageHeaderText: {
      fontSize: "22px",
      fontWeight: "700",
      color: "#000000",
      letterSpacing: "1px",
      textTransform: "uppercase",
      margin: 0,
    },
    contentBody: {
      padding: "24px",
    },
    sectionTitle: {
      fontSize: "15px",
      fontWeight: "700",
      color: "#000000",
      marginBottom: "4px",
    },
    sectionDivider: {
      border: "none",
      borderTop: "2px solid #FFC107",
      marginBottom: "16px",
    },
    inputRow: {
      display: "grid",
      gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
      gap: "16px",
      marginBottom: "16px",
    },
    fieldGroup: {
      display: "flex",
      flexDirection: "column",
      gap: "4px",
    },
    label: {
      fontSize: "13px",
      fontWeight: "700",
      color: "#000000",
    },
    input: {
      padding: "8px 10px",
      border: "1.5px solid #000000",
      borderRadius: "0",
      fontSize: "13px",
      backgroundColor: "#ffffff",
      color: "#000000",
      outline: "none",
      boxSizing: "border-box",
    },
    inputDisabled: {
      padding: "8px 10px",
      border: "1.5px solid #000000",
      borderRadius: "0",
      fontSize: "13px",
      backgroundColor: "#f5f5f5",
      color: "#000000",
      outline: "none",
      boxSizing: "border-box",
      cursor: "not-allowed",
    },
    statusBox: {
      border: "1.5px solid #000000",
      backgroundColor: tracking ? "#fffde7" : "#f5f5f5",
      padding: "20px 24px",
      textAlign: "center",
      marginBottom: "16px",
    },
    pulseWrap: {
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      gap: "8px",
      fontSize: "13px",
      color: "#000000",
      marginBottom: "12px",
      fontWeight: "700",
    },
    pulse: {
      width: "12px",
      height: "12px",
      borderRadius: "50%",
      backgroundColor: tracking ? "#FFC107" : "#cccccc",
      display: "inline-block",
      animation: tracking ? "blink 1s infinite" : "none",
    },
    coordGrid: {
      display: "grid",
      gridTemplateColumns: "repeat(3, 1fr)",
      gap: "12px",
      marginBottom: "16px",
    },
    coordCard: {
      border: "1.5px solid #000000",
      padding: "12px",
      textAlign: "center",
      backgroundColor: "#ffffff",
    },
    coordVal: {
      fontSize: "15px",
      fontWeight: "700",
      color: "#000000",
      fontVariantNumeric: "tabular-nums",
    },
    coordLabel: {
      fontSize: "11px",
      color: "#555555",
      marginTop: "4px",
      fontWeight: "700",
    },
    btnRow: {
      display: "flex",
      gap: "12px",
      justifyContent: "center",
      marginTop: "8px",
    },
    startBtn: {
      backgroundColor: tracking ? "#cccccc" : "#FFC107",
      color: "#000000",
      border: "none",
      padding: "8px 28px",
      borderRadius: "0",
      cursor: tracking ? "not-allowed" : "pointer",
      fontWeight: "700",
      fontSize: "13px",
      textTransform: "uppercase",
    },
    stopBtn: {
      backgroundColor: !tracking ? "#cccccc" : "#000000",
      color: !tracking ? "#000000" : "#ffffff",
      border: "none",
      padding: "8px 28px",
      borderRadius: "0",
      cursor: !tracking ? "not-allowed" : "pointer",
      fontWeight: "700",
      fontSize: "13px",
      textTransform: "uppercase",
    },
    table: {
      width: "100%",
      borderCollapse: "collapse",
      marginTop: "8px",
    },
    th: {
      backgroundColor: "#000000",
      color: "#ffffff",
      padding: "12px 16px",
      textAlign: "center",
      fontSize: "13px",
      fontWeight: "700",
      border: "1px solid #000000",
    },
    td: {
      border: "1px solid #000000",
      padding: "10px 14px",
      fontSize: "13px",
      textAlign: "center",
      backgroundColor: "#ffffff",
      color: "#000000",
      fontVariantNumeric: "tabular-nums",
    },
    tdHighlight: {
      border: "1px solid #000000",
      padding: "10px 14px",
      fontSize: "13px",
      textAlign: "center",
      backgroundColor: "#fffde7",
      color: "#000000",
      fontVariantNumeric: "tabular-nums",
    },
  };

  return (
    <div style={s.container}>
      <style>{`@keyframes blink{0%,100%{opacity:1}50%{opacity:0.3}}`}</style>

      {/* Yellow header bar */}
      <div style={s.pageHeader}>
        <h1 style={s.pageHeaderText}>Driver Live Tracking</h1>
      </div>

      <div style={s.contentBody}>

        {/* Driver Info */}
        <div style={s.sectionTitle}>Driver Information</div>
        <hr style={s.sectionDivider} />
        <div style={s.inputRow}>
          <div style={s.fieldGroup}>
            <label style={s.label}>Driver Name</label>
            <input
              style={tracking ? s.inputDisabled : s.input}
              value={driverName}
              onChange={(e) => setDriverName(e.target.value)}
              placeholder="Enter driver name"
              disabled={tracking}
            />
          </div>
          <div style={s.fieldGroup}>
            <label style={s.label}>Vehicle Number</label>
            <FleetVehicleSelect
              name="vehicleNo"
              value={vehicleNo}
              onChange={(e) => setVehicleNo(e.target.value)}
              style={tracking ? s.inputDisabled : s.input}
              disabled={tracking}
              placeholderOption="Select vehicle"
            />
          </div>
        </div>

        {/* Tracking Status */}
        <div style={s.sectionTitle}>Tracking Status</div>
        <hr style={s.sectionDivider} />
        <div style={s.statusBox}>
          <div style={s.pulseWrap}>
            <span style={s.pulse}></span>
            {tracking ? "Live location tracking active" : "Tracking inactive — press Start to begin"}
          </div>

          {error && (
            <div style={{ color: "#000000", fontSize: "12px", marginBottom: "10px", fontWeight: "700" }}>
              ⚠ {error}
            </div>
          )}

          {location && (
            <div style={s.coordGrid}>
              <div style={s.coordCard}>
                <div style={s.coordVal}>{location.lat.toFixed(5)}</div>
                <div style={s.coordLabel}>Latitude</div>
              </div>
              <div style={s.coordCard}>
                <div style={s.coordVal}>{location.lng.toFixed(5)}</div>
                <div style={s.coordLabel}>Longitude</div>
              </div>
              <div style={s.coordCard}>
                <div style={s.coordVal}>±{location.accuracy}m</div>
                <div style={s.coordLabel}>Accuracy</div>
              </div>
            </div>
          )}

          <div style={s.btnRow}>
            <button style={s.startBtn} disabled={tracking} onClick={handleStart}>
              ▶ Start Tracking
            </button>
            <button style={s.stopBtn} disabled={!tracking} onClick={handleStop}>
              ■ Stop Tracking
            </button>
          </div>
        </div>

        {/* Location History */}
        {history.length > 0 && (
          <>
            <div style={{ ...s.sectionTitle, marginTop: "24px" }}>Location History</div>
            <hr style={s.sectionDivider} />
            <table style={s.table}>
              <thead>
                <tr>
                  {["Time", "Latitude", "Longitude", "Accuracy"].map((h) => (
                    <th style={s.th} key={h}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {history.map((h, i) => (
                  <tr key={i}>
                    <td style={i === 0 ? s.tdHighlight : s.td}>{h.time}</td>
                    <td style={i === 0 ? s.tdHighlight : s.td}>{h.lat.toFixed(6)}</td>
                    <td style={i === 0 ? s.tdHighlight : s.td}>{h.lng.toFixed(6)}</td>
                    <td style={i === 0 ? s.tdHighlight : s.td}>±{h.accuracy}m</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </>
        )}
      </div>
    </div>
  );
};

export default DriverLiveTracking;